import { type FC, type ReactNode, useEffect, useRef } from 'react';
import { HideFiltersTab } from '@components/HideFiltersTab';
import { Button } from '@components/ui/button';
import type { ResolvedSchemaTree } from '@root/helpers/schema/types';
import { Plus, XCircle } from 'lucide-react';

import { QueryBuilderRow } from '../components/QueryBuilderRow';
import type { FieldDef, FilterRow, OptionsRecord } from '../types';

interface QueryBuilderPanelProps {
  datapointFieldDefMap: Map<string, FieldDef>;
  datapointTree: ResolvedSchemaTree[];
  disableAddFilter: boolean;
  contractDetailsFields: FieldDef[];
  blockFields: FieldDef[];
  /** Rendered as-is inside the panel card, above the filter rows. Slot content owns its spacing. */
  headerSlot?: ReactNode;
  /** Rendered as-is inside the panel card, below the filter rows. Slot content owns its spacing and divider. */
  footerSlot?: ReactNode;
  onAddRow: () => void;
  onClearAll: () => void;
  onHide: () => void;
  onRemoveRow: (rowId: string) => void;
  onRowUpdate: (rowId: string, patch: Partial<FilterRow>) => void;
  optionsRecord: OptionsRecord;
  rows: FilterRow[];
}

export const QueryBuilderPanel: FC<QueryBuilderPanelProps> = ({
  datapointFieldDefMap,
  datapointTree,
  disableAddFilter,
  contractDetailsFields,
  blockFields,
  headerSlot,
  footerSlot,
  onAddRow,
  onClearAll,
  onHide,
  onRemoveRow,
  onRowUpdate,
  optionsRecord,
  rows,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  const rowsCount = rows.length;
  const prevRowsCountRef = useRef(rowsCount);

  useEffect(() => {
    if (panelRef.current && rowsCount > prevRowsCountRef.current) {
      panelRef.current.scrollTo({ top: panelRef.current.scrollHeight, behavior: 'smooth' });
    }
    prevRowsCountRef.current = rowsCount;
  }, [rowsCount]);

  return (
    <>
      <div
        data-testid="filters-bar"
        className="filters-bar mt-4 border border-gray-200 bg-gray-50 p-4 pt-1 md:rounded-lg"
      >
        {headerSlot}
        <div className="flex flex-row items-start gap-6">
          <p className="text-info-700 mt-3 w-24 py-2 text-sm/6 font-medium">FILTERS</p>
          <div className="flex w-full min-w-0 flex-col gap-5 pt-3">
            <div ref={panelRef} className="flex max-h-60 flex-col gap-5 overflow-y-auto">
              {rows.map((row) => (
                <QueryBuilderRow
                  key={row.id}
                  row={row}
                  contractDetailsFields={contractDetailsFields}
                  blockFields={blockFields}
                  datapointTree={datapointTree}
                  datapointFieldDefMap={datapointFieldDefMap}
                  optionsRecord={optionsRecord}
                  onRowUpdate={onRowUpdate}
                  onRemove={onRemoveRow}
                />
              ))}
            </div>

            <div className="flex items-center justify-between pb-2">
              <Button variant="primary" size="default" onClick={onAddRow} disabled={disableAddFilter}>
                <Plus className="size-4" />
                Add a filter
              </Button>
              <Button variant="outline" onClick={onClearAll} data-testid="clear-all-filters">
                <XCircle aria-hidden="true" />
                Clear all filters
              </Button>
            </div>
          </div>
        </div>
        {footerSlot}
      </div>
      <HideFiltersTab onHide={onHide} />
    </>
  );
};
